import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as ActivityIcon } from '../../assets/icons/activety-icon.svg';
import { ReactComponent as CalendarIcon } from '../../assets/icons/calender.svg';
import { ReactComponent as CarIcon } from '../../assets/icons/car.svg';
import { ReactComponent as GraphIcon } from '../../assets/icons/chart-icon.svg';
import { ReactComponent as ClockIcon } from '../../assets/icons/clock-icon.svg';
import { ReactComponent as CurrencyIcon } from '../../assets/icons/currentPrice.svg';
import { ReactComponent as LightningIcon } from '../../assets/icons/flash-icon.svg';
import { ReactComponent as HistoryIcon } from '../../assets/icons/history-icon.svg';
import { ReactComponent as UserIcon } from '../../assets/icons/user-icon.svg';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import FormatDuration from '../../shared-functions/FormatDuration';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import emspSessionApi from '../../utils/api/v2/emsp-session';
import Copy from '../copy/Copy';
import TabToggle from '../tab-toggle/TabToggle';

interface EmspSessionSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
}

/**
 * Formats ISO date string to "DD MMM YYYY, HH:MM AM/PM" format
 * @param {string | undefined} dateString - The ISO date string
 * @returns {string} The formatted date string
 */
const formatDateTime = (dateString: string | undefined): string => {
  if (!dateString) return '—';
  try {
    const dateValue = ConvertUTCtoDate(dateString);
    const timeValue = FormatUtcString(ConvertUTCtoHHMM(dateString));
    if (dateValue && timeValue) {
      return `${dateValue}, ${timeValue}`;
    }
  } catch (error) {
    console.error(error);
  }
  return dateString;
};

/**
 * EmspSessionSidePanel component to display detail card information for an eMSP Session.
 * @param {EmspSessionSidePanelProperties} properties - Component properties.
 * @returns {any} The rendered side panel.
 */
function EmspSessionSidePanel(properties: EmspSessionSidePanelProperties) {
  const { t } = useTranslation('sidePanel');
  const { fetchIdFrom, id } = properties;

  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();
  const [sessionData, setSessionData] = useState<any>();
  const [activeTab, setActiveTab] = useState<'session' | 'user'>('session');

  /**
   * Helper function to get nested values from sessionData using dot notation
   * @param {string} path - dot-separated key path
   * @returns {any} value or fallback string
   */
  const getNestedValue = (path: string): any => {
    let nestedValue: any = sessionData;
    try {
      const parts = path.split('.');
      parts.forEach((part) => {
        if (nestedValue !== null && nestedValue !== undefined) {
          const snakePart = part.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
          const resolvedKey = [part, snakePart].find(
            (key) => nestedValue[key] !== undefined && nestedValue[key] !== null,
          );
          nestedValue = resolvedKey !== undefined ? nestedValue[resolvedKey] : undefined;
        }
      });
    } catch (error) {
      console.error(error);
    }
    return nestedValue !== undefined && nestedValue !== null ? nestedValue : '—';
  };

  /**
   * Function to fetch EMSP session details
   * @param {string} sessionId - The session ID
   * @returns {Promise<void>} Resolves when fetch completes
   */
  const handleFetchSessionDetails = async (sessionId: string) => {
    emspSessionApi
      .fetchCommercialSessionById(sessionId)
      .then((response) => {
        setSessionData(response?.data);
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('failedToFetchSessionDetails'),
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  useEffect(() => {
    let sessionId;
    if (fetchIdFrom === 'queryParams') {
      sessionId = searchParameters.get('sessionId') || searchParameters.get('id');
    } else if (fetchIdFrom === 'props') {
      sessionId = id;
    }

    if (!sessionId) {
      return;
    }

    setActiveTab('session');
    handleFetchSessionDetails(sessionId);
  }, [searchParameters, id]);

  const statusValue = getNestedValue('status');

  const energyValue = sessionData?.totalEnergy ?? sessionData?.energyKwh;
  const formattedEnergy =
    energyValue !== undefined && energyValue !== null
      ? `${ToFixedValue(Number(energyValue), 3)} kWh`
      : '—';

  const rawCurrency = getNestedValue('currency');
  const currencyValue =
    rawCurrency !== '—' && rawCurrency ? rawCurrency : sessionData?.currency || 'SAR';

  let rawCost: any =
    sessionData?.liveCost !== undefined && sessionData?.liveCost !== null
      ? sessionData.liveCost
      : getNestedValue('liveCost');

  if (rawCost === '—') {
    rawCost = sessionData?.totalCost ?? sessionData?.cost ?? getNestedValue('totalCost');
  }

  if (rawCost && typeof rawCost === 'object') {
    rawCost = rawCost.incl_vat ?? rawCost.excl_vat ?? rawCost.amount;
  }

  const formattedLiveCost =
    rawCost !== undefined && rawCost !== null && rawCost !== '—' && !Number.isNaN(Number(rawCost))
      ? `${ToFixedValue(Number(rawCost), 2)} ${currencyValue}`
      : '—';

  const sessionInfoList = [
    {
      label: t('sessionId'),
      value:
        getNestedValue('sessionId') !== '—' ? getNestedValue('sessionId') : getNestedValue('id'),
      copy: true,
    },
    { label: t('evseId'), value: getNestedValue('evseUid') },
    {
      label: t('connectorId'),
      value:
        getNestedValue('connector') !== '—'
          ? getNestedValue('connector')
          : getNestedValue('connectorId'),
    },
    {
      label: t('locationId'),
      value:
        getNestedValue('location.id') !== '—'
          ? getNestedValue('location.id')
          : getNestedValue('locationId'),
    },
    { label: t('partyId'), value: getNestedValue('partyId') },
    {
      label: t('status'),
      value: statusValue,
      isBadge: true,
    },
    {
      label: t('startTime'),
      value: formatDateTime(sessionData?.startTime || sessionData?.sessionStartAt),
    },
    {
      label: t('endTime'),
      value: formatDateTime(sessionData?.endTime || sessionData?.sessionEndAt),
    },
    {
      label: t('stopSource'),
      value: getNestedValue('stopSource'),
    },
    {
      label: t('stoppedByOperatorId'),
      value: getNestedValue('stoppedByOperatorId'),
    },
  ];

  const rawUserName = getNestedValue('userName');
  let displayUserName = '—';
  if (rawUserName !== '—') {
    displayUserName = rawUserName;
  } else {
    const firstName = getNestedValue('driver.firstName');
    const lastName = getNestedValue('driver.lastName');
    if (firstName !== '—') {
      displayUserName = `${firstName} ${lastName !== '—' ? lastName : ''}`.trim();
    }
  }

  /**
   * Helper function to extract raw phone string without nested ternaries.
   * @returns {string} Raw phone number.
   */
  const getRawPhone = (): string => {
    const driverPhone = getNestedValue('driver.phoneNumber');
    if (driverPhone !== '—') return driverPhone;
    const driverPhoneAlt = getNestedValue('driver.phone');
    if (driverPhoneAlt !== '—') return driverPhoneAlt;
    const rootPhone = getNestedValue('phoneNumber');
    if (rootPhone !== '—') return rootPhone;
    return getNestedValue('phone');
  };

  const rawPhone = getRawPhone();

  const rawCountryCode =
    getNestedValue('driver.countryCode') !== '—'
      ? getNestedValue('driver.countryCode')
      : getNestedValue('countryCode');

  let formattedPhone = '—';
  if (rawPhone !== '—' && rawPhone) {
    let cleanPhone = String(rawPhone).trim();
    if (rawCountryCode !== '—' && rawCountryCode) {
      const codeString = String(rawCountryCode).trim().replace(/^\+/, '');
      const codeWithPlus = `+${codeString}`;

      if (cleanPhone.startsWith(codeString)) {
        cleanPhone = cleanPhone.slice(codeString.length).trim();
      } else if (cleanPhone.startsWith(`+${codeString}`)) {
        cleanPhone = cleanPhone.slice(codeString.length + 1).trim();
      }

      formattedPhone = `${codeWithPlus} ${cleanPhone}`;
    } else {
      formattedPhone = cleanPhone;
    }
  }

  const driverInfoList = [
    {
      label: t('userCustomId'),
      value:
        getNestedValue('driver.userCustomId') !== '—'
          ? getNestedValue('driver.userCustomId')
          : getNestedValue('userCustomId'),
    },
    { label: t('userName'), value: displayUserName },
    {
      label: t('email'),
      value:
        getNestedValue('driver.email') !== '—'
          ? getNestedValue('driver.email')
          : getNestedValue('email'),
    },
    { label: t('phoneNumber'), value: formattedPhone },
  ];

  const vehicleInfoList = [
    {
      label: t('manufacturer'),
      value: getNestedValue('vehicle.manufacturer'),
    },
    { label: t('model'), value: getNestedValue('vehicle.model') },
    {
      label: t('manufacturerYear'),
      value: getNestedValue('vehicle.manufacturerYear'),
    },
    {
      label: t('batteryCapacity'),
      value:
        getNestedValue('vehicle.batteryCapacity') !== '—'
          ? `${ToFixedValue(Number(getNestedValue('vehicle.batteryCapacity')), 3)} kWh`
          : '—',
    },
    {
      label: t('connectorType'),
      value: getNestedValue('vehicle.connectorType'),
    },
  ];

  const summaryList = [
    { label: t('energyKwh'), value: formattedEnergy },
    {
      label: t('preferredPaymentMethod'),
      value:
        getNestedValue('preferredPaymentMethod') !== '—'
          ? getNestedValue('preferredPaymentMethod')
          : getNestedValue('paymentMethod'),
    },
    // { label: t('totalCost'), value: formattedCost },
    // { label: t('totalCostCurrency'), value: formattedCost },
  ];

  const timestampList = [
    {
      label: t('createdAt'),
      value: formatDateTime(sessionData?.createdAt),
    },
    {
      label: t('lastUpdated'),
      value: formatDateTime(sessionData?.updatedAt || sessionData?.lastUpdated),
    },
  ];

  const sessionSections = [
    {
      header: t('sessionInformation'),
      icon: <CalendarIcon />,
      items: sessionInfoList,
      classKey: 'session-info',
      tab: 'session',
    },
    {
      header: t('driverInformation'),
      icon: <UserIcon />,
      items: driverInfoList,
      classKey: 'driver-info',
      tab: 'user',
    },
    {
      header: t('vehicleInformation'),
      icon: <CarIcon />,
      items: vehicleInfoList,
      classKey: 'vehicle-info',
      tab: 'user',
    },
    {
      header: t('sessionSummary'),
      icon: <GraphIcon />,
      items: summaryList,
      classKey: 'session-summary',
      tab: 'session',
    },
    {
      header: t('timestamps'),
      icon: <ClockIcon />,
      items: timestampList,
      classKey: 'timestamps',
      tab: 'session',
    },
  ];

  const activeSections = sessionSections.filter((section) => section.tab === activeTab);

  /**
   * Helper function to render an item's value or status badge with optional copy icon.
   * @param {any} item - The field item to render.
   * @returns {React.JSX.Element} The rendered item content element.
   */
  const renderItemContent = (item: any): React.JSX.Element => {
    if (item.isBadge) {
      return (
        <span
          className={`status-pill ${String(item.value).toLowerCase().replace(/\s+/g, '-')}`}>
          {item.value}
        </span>
      );
    }

    if (item.copy && item.value && item.value !== '—') {
      return (
        <div className="item-value-wrap">
          <span className="item-value">{item.value}</span>
          <Copy
            value={item.value}
            size="small"
            hoverLabel={t('copyValue', { label: item.label })}
          />
        </div>
      );
    }

    return <span className="item-value">{item.value}</span>;
  };

  return (
    <div className="emsp-session-side-panel-wrap">
      {/* Scrollable Middle Content */}
      <div className="panel-scrollable-content">
        {/* 4-Metric summary row */}
        <div className="metrics-summary-row">
          {/* Card 1: Status */}
          <div
            className="metric-card"
            title={statusValue ? statusValue.charAt(0).toUpperCase() + statusValue.slice(1) : ''}>
            <div
              className={`badge-icon-wrap status status-${String(statusValue).toLowerCase().replace(/\s+/g, '-')}`}>
              {['finished', 'completed'].includes(String(statusValue).toLowerCase()) ? (
                <HistoryIcon />
              ) : (
                <ActivityIcon />
              )}
            </div>
            <div className="card-info">
              <span className="card-title">{t('statusLabel')}</span>
              <span
                className={`card-value status-${String(statusValue).toLowerCase().replace(/\s+/g, '-')}`}
                title={
                  statusValue ? statusValue.charAt(0).toUpperCase() + statusValue.slice(1) : ''
                }>
                {statusValue ? statusValue.charAt(0).toUpperCase() + statusValue.slice(1) : '—'}
              </span>
              {String(statusValue).toLowerCase() === 'in progress' && (
                <span className="active-session-badge">{t('activeSession')}</span>
              )}
            </div>
          </div>

          {/* Card 2: Duration */}
          <div
            className="metric-card"
            title={FormatDuration(sessionData?.durationSeconds, { formatBy: 'sec' })}>
            <div className="badge-icon-wrap duration">
              <ClockIcon />
            </div>
            <div className="card-info">
              <span className="card-title">{t('durationLabel')}</span>
              <span
                className="card-value"
                title={FormatDuration(sessionData?.durationSeconds, { formatBy: 'sec' })}>
                {FormatDuration(sessionData?.durationSeconds, { formatBy: 'sec' })}
              </span>
              <span className="card-subtext">
                {FormatDuration(sessionData?.durationSeconds, { formatBy: 'sec' }) === 'N/A'
                  ? t('notAvailableText')
                  : t('totalTime')}
              </span>
            </div>
          </div>

          {/* Card 3: Energy */}
          <div className="metric-card" title={formattedEnergy}>
            <div className="badge-icon-wrap energy">
              <LightningIcon />
            </div>
            <div className="card-info">
              <span className="card-title">{t('energyLabel')}</span>
              <span className="card-value" title={formattedEnergy}>
                {formattedEnergy}
              </span>
              <span className="card-subtext" title={t('totalEnergy')}>
                {t('totalEnergy')}
              </span>
            </div>
          </div>

          {/* Card 4: Live Cost */}
          <div className="metric-card" title={formattedLiveCost}>
            <div className="badge-icon-wrap currency">
              <CurrencyIcon />
            </div>
            <div className="card-info">
              <span className="card-title">{t('liveCost', { defaultValue: 'LIVE COST' })}</span>
              <span className="card-value" title={formattedLiveCost}>
                {formattedLiveCost}
              </span>
              <span className="card-subtext" title={t('totalCost', { defaultValue: 'Total Cost' })}>
                {formattedLiveCost === '—'
                  ? 'Not Available'
                  : t('totalCost', { defaultValue: 'Total Cost' })}
              </span>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="panel-tab-toggle-wrap">
          <TabToggle
            tabs={[
              {
                label: t('sessionDetails'),
                value: 'session',
              },
              {
                label: t('userDetails'),
                value: 'user',
              },
            ]}
            activeTab={activeTab}
            defaultTab="session"
            isLabelValue
            onChange={(value) => setActiveTab(value as 'session' | 'user')}
          />
        </div>

        {/* Details Sections */}
        {activeSections.map((section, index) => (
          <div className="detail-section-card" key={index}>
            <div className={`card-header ${section.classKey}`}>
              {section.icon}
              <h3>{section.header}</h3>
            </div>
            <div className="card-body-grid">
              {section.items.map((item: any, itemIndex: number) => (
                <div className="grid-item" key={itemIndex}>
                  <span className="item-label">{item.label}</span>
                  {renderItemContent(item)}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default EmspSessionSidePanel;
